// Role-aware tour step definitions + help-center content.
// Pure data only — no React, no side effects — so it can be imported anywhere
// and tree-shaken cleanly. Content is structured for future expansion.

import type { GuidanceRole } from '@/lib/onboarding';

export interface TourStep {
  title: string;
  body: string;
  /** Optional dashboard tab key consumers may jump to. */
  jumpTo?: string;
}

export interface RoleTour {
  role: GuidanceRole;
  label: string;
  welcomeTitle: string;
  welcomeBody: string;
  steps: TourStep[];
}

/* ──────────── role-based guided tours ──────────── */

export const ROLE_TOURS: Record<GuidanceRole, RoleTour> = {
  admin: {
    role: 'admin',
    label: 'Administrator',
    welcomeTitle: 'Welcome to the GIISDEC Administration Portal',
    welcomeBody:
      'This guided orientation highlights the executive readiness, user, role, audit, GIS, and investor-oversight areas available to administrators.',
    steps: [
      { title: 'Dashboard overview', body: 'Your landing workspace summarises platform health, readiness signals, and recent activity at a glance.' },
      { title: 'Executive readiness', body: 'Readiness widgets aggregate pilot-readiness indicators so leadership can review go/no-go status quickly.', jumpTo: 'readiness' },
      { title: 'Users management', body: 'Review and manage platform users. Account creation and role assignment remain governed by RBAC.' },
      { title: 'Roles & permissions', body: 'Inspect role definitions and permission scopes. primaryRole, role, and roles[] are all honoured.' },
      { title: 'Audit logs', body: 'Every KYC update, approval, rejection, stage progression, and document event is recorded for compliance review.' },
      { title: 'GIS overview', body: 'Access geospatial dashboards and concession layers you are authorized to view.' },
      { title: 'Investor oversight', body: 'Monitor investor KYC status, onboarding completeness, and sequential workflow progress (LOI → NDA → Payment → MOU).' },
    ],
  },
  investor: {
    role: 'investor',
    label: 'Investor',
    welcomeTitle: 'Welcome to your GIISDEC Investor Portal',
    welcomeBody:
      'Let us orient you to onboarding, KYC, and the official investor workflow so you always know your next step.',
    steps: [
      { title: 'Dashboard overview', body: 'Your overview shows status, required next action, and a live workflow progress tracker.', jumpTo: 'overview' },
      { title: 'KYC progress', body: 'Complete your enhanced KYC profile. A completeness meter shows what remains.', jumpTo: 'kyc' },
      { title: 'LOI process', body: 'The Letter of Intent is the first governed stage. Submit it to unlock the next steps.', jumpTo: 'agreements' },
      { title: 'NDA process', body: 'After your LOI, the NDA becomes available for issuance and acceptance.', jumpTo: 'agreements' },
      { title: 'Payment workflow', body: 'Validated payments are required before the MOU stage opens.', jumpTo: 'payments' },
      { title: 'MOU workflow', body: 'The Memorandum of Understanding unlocks once prior stages are complete.', jumpTo: 'agreements' },
      { title: 'Document center', body: 'Upload supporting documents and track signature & approval status here.', jumpTo: 'documents' },
    ],
  },
  operations: {
    role: 'operations',
    label: 'Operations',
    welcomeTitle: 'Welcome to the GIISDEC Operations Workspace',
    welcomeBody: 'This orientation covers monitoring, approvals, workflow tracking, and notifications.',
    steps: [
      { title: 'Monitoring areas', body: 'Track active applications and workspace activity across the pilot.' },
      { title: 'Approvals', body: 'Review pending items and record approval or rejection decisions — all audited.' },
      { title: 'Workflow tracking', body: 'Follow investor progress through the LOI → NDA → Payment → MOU sequence.' },
      { title: 'Notifications', body: 'Stay informed of status changes and required actions across the platform.' },
    ],
  },
  gis: {
    role: 'gis',
    label: 'GIS Analyst',
    welcomeTitle: 'Welcome to the GIISDEC GIS Workspace',
    welcomeBody: 'A quick orientation to the geospatial dashboards and data layers.',
    steps: [
      { title: 'GIS dashboard', body: 'Your analytical workspace for concession layers and mineral-interest data.' },
      { title: 'Map layers', body: 'Toggle and inspect the map layers you are authorized to view.' },
      { title: 'Uploads & datasets', body: 'Manage geospatial datasets with graceful empty states when data is unavailable.' },
    ],
  },
  mobile: {
    role: 'mobile',
    label: 'Mobile User',
    welcomeTitle: 'Welcome to the GIISDEC Field App',
    welcomeBody: 'A short orientation for logging in and working on the go — including offline sync.',
    steps: [
      { title: 'Login', body: 'Sign in with your GIISDEC credentials. Sessions are kept secure on device.' },
      { title: 'Permissions & location', body: 'Grant location access to enable field-capture and location services.' },
      { title: 'Uploads', body: 'Capture and upload documents or field data; uploads queue safely when offline.' },
      { title: 'Notifications', body: 'Receive status updates and required-action alerts.' },
      { title: 'Offline sync', body: 'Work offline — your data synchronises automatically when connectivity returns.' },
    ],
  },
  public: {
    role: 'public',
    label: 'Visitor',
    welcomeTitle: 'Welcome to GIISDEC GIIP',
    welcomeBody: 'Explore public information about the Ghana Integrated Iron & Steel Development Corporation.',
    steps: [
      { title: 'Explore', body: 'Browse projects, services, and corporate information.' },
      { title: 'Sign in', body: 'Investors, analysts, and administrators sign in to access their secure workspaces.' },
    ],
  },
};

/* ──────────── help-center content ──────────── */

export interface HelpTopic {
  id: string;
  title: string;
  summary: string;
  points: string[];
}

export const HELP_TOPICS: HelpTopic[] = [
  {
    id: 'getting-started',
    title: 'Getting started',
    summary: 'Orient yourself in the GIISDEC platform.',
    points: [
      'Use the role-specific guided tour from the help launcher at any time.',
      'Your dashboard always surfaces a recommended next action.',
      'Need to revisit the welcome orientation? Use “Replay tour” in the help launcher.',
    ],
  },
  {
    id: 'platform-overview',
    title: 'Platform overview',
    summary: 'How the GIISDEC ecosystem fits together.',
    points: [
      'The Public Portal, Investor Portal, Administration Portal and GIISDEC Field App share one secure GIISDEC GIIP service.',
      'Readiness panels and diagnostics help confirm the platform is operating correctly.',
      'Empty states explain what is missing and recommend a next step.',
    ],
  },
  {
    id: 'investor-onboarding',
    title: 'Investor onboarding',
    summary: 'Complete KYC and progress through the official workflow.',
    points: [
      'Fill the enhanced KYC profile — a completeness meter tracks your progress.',
      'The official sequence is LOI → NDA → Payment Validation → MOU.',
      'Each stage unlocks only after the previous one is complete.',
      'Document signature and approval status is visible in the document center.',
    ],
  },
  {
    id: 'gis-usage',
    title: 'GIS usage',
    summary: 'Work with geospatial dashboards and layers.',
    points: [
      'Access concession and mineral-interest layers from the GIS dashboard.',
      'Toggle layers to focus your analysis.',
      'All GIS data is served securely by GIISDEC, according to your access.',
    ],
  },
  {
    id: 'document-management',
    title: 'Document management',
    summary: 'Upload, track, and review documents.',
    points: [
      'Upload supporting documents from the document center.',
      'Track upload, signature, approval, and rejection status per document.',
      'Restricted data requires the appropriate agreements to be in place.',
    ],
  },
  {
    id: 'account-management',
    title: 'Account management',
    summary: 'Manage your profile and access.',
    points: [
      'Your role determines which workspaces and actions are available (RBAC).',
      'Contact the GIISDEC team via the support area for access changes.',
      'Sign out securely from the navigation menu.',
    ],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    summary: 'Stay informed of important updates.',
    points: [
      'Status changes and required actions generate notifications.',
      'Manage your notification preferences in the support area.',
      'Notification history is available in your workspace.',
    ],
  },
  {
    id: 'faq',
    title: 'Frequently asked questions',
    summary: 'Quick answers to common questions.',
    points: [
      'Why is a stage locked? Earlier stages in the LOI → NDA → Payment → MOU sequence must be completed first.',
      'Why is some data empty? The data may not be available yet — empty states recommend your next step.',
      'How do I replay the tour? Open the help launcher and choose “Replay tour”.',
    ],
  },
  {
    id: 'gis-readiness',
    title: 'GIS readiness',
    summary: 'Understand how GIS data becomes available.',
    points: [
      'Readiness covers Data, Metadata, Publication, Field, and Integration dimensions.',
      'When readiness data is unavailable for your account, a public-safe explanation is shown.',
      'Approved datasets and layers appear as GIISDEC validates and releases them.',
    ],
  },
  {
    id: 'layer-publication-status',
    title: 'Layer publication status',
    summary: 'How datasets progress to publication.',
    points: [
      'The lifecycle is Pending Validation → Under Review → Approved → Published.',
      'Datasets may also be Restricted or Archived depending on classification.',
      'Layer categories show “Pending GIISDEC Validation” until live data exists.',
    ],
  },
  {
    id: 'restricted-layers',
    title: 'Restricted layers',
    summary: 'Why some GIS layers are locked.',
    points: [
      'Restricted layers display as locked until GIISDEC grants access.',
      'Access enforcement is performed server-side — the portal never bypasses it.',
      'Investors only ever see public-safe layer and readiness information.',
    ],
  },
  {
    id: 'geojson-postgis-readiness',
    title: 'Spatial data delivery',
    summary: 'How approved spatial layers will be delivered.',
    points: [
      'Approved layers are delivered in a standard geospatial (GeoJSON) format.',
      'Supported fields include layer id, name, classification, geometry type, bbox, source, status, and access level.',
      'The map viewer renders geometry only once a validated GeoJSON source is published.',
    ],
  },
  {
    id: 'dataset-validation',
    title: 'Dataset validation',
    summary: 'How GIS datasets are validated before release.',
    points: [
      'Import readiness covers GeoJSON, Shapefile, KML/KMZ, and CSV coordinate sources.',
      'Quality gates include geometry validation, metadata completeness, and classification review.',
      'Publication approval is the final gate before a dataset becomes available.',
    ],
  },
  {
    id: 'loi-guide',
    title: 'Letter of Intent (LOI) guide',
    summary: 'The first governed stage of the investor workflow.',
    points: [
      'Submit your LOI from the NDA / MOU / LOI tab once your profile is ready.',
      'The LOI must be accepted before the NDA stage becomes available.',
      'Track LOI status and signatures in the Agreement Centre.',
    ],
  },
  {
    id: 'nda-guide',
    title: 'Non-Disclosure Agreement (NDA) guide',
    summary: 'Protects confidential geological and commercial information.',
    points: [
      'The NDA becomes available after your LOI is completed.',
      'Both investor and GIISDEC signature statuses are shown in the Agreement Centre.',
      'A signed NDA unlocks NDA-classified documents and datasets.',
    ],
  },
  {
    id: 'payment-validation-guide',
    title: 'Payment Validation guide',
    summary: 'How invoices and Finance payment verification work.',
    points: [
      'Invoices appear in the Payments tab with amount, due date, and balance.',
      'Payment validation is required before the MOU stage opens.',
      'There is no online checkout. Pay the invoice as instructed by GIISDEC, then submit your payment evidence (reference and proof) in Payments. GIISDEC Finance verifies each payment manually; until then it shows as Pending Finance verification.',
    ],
  },
  {
    id: 'mou-guide',
    title: 'Memorandum of Understanding (MOU) guide',
    summary: 'The final stage of the official investor workflow.',
    points: [
      'The MOU unlocks after LOI, NDA, and Payment Validation are complete.',
      'MOU status, effective date, and signatures are shown in the Agreement Centre.',
      'A signed MOU unlocks MOU-classified data access.',
    ],
  },
  {
    id: 'agreement-centre',
    title: 'Agreement Centre',
    summary: 'One consolidated view of LOI, NDA, and MOU.',
    points: [
      'Every agreement shows type, reference, status, both signature statuses, and dates.',
      'View or download actions appear when GIISDEC authorises them.',
      'Locked agreements explain which preceding stage must be completed first.',
    ],
  },
  {
    id: 'invoice-payment-centre',
    title: 'Invoice & Payment Centre',
    summary: 'Track invoices, balances, and receipts in one place.',
    points: [
      'Each invoice lists number, description, dates, total, amount paid, and balance.',
      'Receipts are retained in the Document Centre once payments are validated.',
      'No invoice yet? You will be notified when a payment is required.',
    ],
  },
  {
    id: 'document-centre',
    title: 'Document Centre',
    summary: 'All your documents, grouped and permission-aware.',
    points: [
      'Documents are grouped into investor, KYC, agreements, invoices, receipts, project, GIS, and correspondence categories.',
      'Verification status is shown per document; access is enforced by GIISDEC.',
      'Signed agreements and receipts are added automatically as stages complete.',
    ],
  },
  {
    id: 'information-requests',
    title: 'Information requests',
    summary: 'Request geological, GIS, project, or restricted data.',
    points: [
      'Submit requests from the Documents, Permits, or Restricted Data tabs.',
      'Track request type, date, status, and outcome in the Applications tab.',
      'Restricted data requests require the appropriate agreements to be in place.',
    ],
  },
  {
    id: 'account-support',
    title: 'Account support',
    summary: 'Get help with your account and workspace.',
    points: [
      'Use the Support tab to raise an enquiry — replies can be routed to email or WhatsApp.',
      'Notification preferences are configurable per channel in the support area.',
      'Sign out securely from the navigation menu when finished.',
    ],
  },
  {
    id: 'sign-in',
    title: 'Sign in',
    summary: 'Access your secure investor or GIS workspace.',
    points: [
      'Use the Investor Portal or GIS Workspace sign-in page with your registered email and password.',
      'After signing in you are returned to the page you originally requested.',
      'If your session expires, you are returned to sign-in safely — simply sign in again.',
    ],
  },
  {
    id: 'forgot-password',
    title: 'Forgotten password',
    summary: 'Securely recover access to your account.',
    points: [
      'Select “Forgot password?” on the sign-in page and enter your registered email address.',
      'If an account exists for that email, reset instructions will be sent — for security, the portal never confirms whether an account exists.',
      'No email? Check your spam folder, confirm the address, wait a few minutes, then request again.',
      'Still stuck? Contact GIISDEC support from the Contact page.',
    ],
  },
  {
    id: 'password-reset',
    title: 'Password reset & requirements',
    summary: 'Set a new password from your reset link.',
    points: [
      'Open the reset link from your email and choose a new password that meets all listed requirements (length, upper/lowercase, number, special character).',
      'Reset links can only be used once and expire after a limited time — an expired or used link shows a friendly message with the option to request a new one.',
      'After a successful reset, sign in with your new password. The portal never stores or displays your password.',
      'Repeated failed sign-ins may temporarily lock your account — use password recovery or contact support.',
    ],
  },
  {
    id: 'phase2-validation',
    title: 'Phase 2 validation',
    summary: 'Participate in Investor & GIS User Portal validation.',
    points: [
      'Phase 2 covers investor sign-in, password recovery, profile, workflow, agreements, payments visibility, documents, notifications, GIS workspace, and information requests.',
      'The Phase 2 Validation Centre on your dashboard lists your role, scope, and suggested scenarios.',
      'Report issues or submit feedback through the Support tab — every submission supports official acceptance.',
    ],
  },
];
